import Siema from 'siema';
import fastdom from 'fastdom';
import breakpoints from '@/js/utils/breakpoints';
import interactiveChildren from '@/js/utils/getInteractiveChildren';

/**
 * Carousel component.
 */
class AICarousel extends HTMLElement {
  /**
   * Observes the specified attributes.
   * @static
   *
   * @return {array} A list of attributes to observe.
   */
  static get observedAttributes() {
    return ['refresh'];
  }

  /**
   * Create the custom element.
   */
  constructor() {
    super();

    // Bind class methods.
    this.conditionallyInit = this.conditionallyInit.bind(this);
    this.resolvePerPageAttribute = this.resolvePerPageAttribute.bind(this);
    this.carouselSetup = this.carouselSetup.bind(this);
    this.getAriaLiveRegion = this.getAriaLiveRegion.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onSlideChange = this.onSlideChange.bind(this);
    this.updateSlideAttributes = this.updateSlideAttributes.bind(this);
    this.updateCarouselButtons = this.updateCarouselButtons.bind(this);
    this.updateAriaLiveRegion = this.updateAriaLiveRegion.bind(this);
    this.cleanup = this.cleanup.bind(this);
    this.destroy = this.destroy.bind(this);

    /**
     * The element containing slides. Can be changed via the `slides` attribute.
     *
     * @type {HTMLElement}
     */
    this.carouselRoot = this;

    /**
     * The classname added when the carousel is initialized.
     *
     * @type {String}
     */
    this.initializedClass = 'ai-carousel--initialized';
  }

  /**
   * Called by the browser when the element is added to the page.
   */
  connectedCallback() {
    /**
     * The `slides` attribute value.
     *
     * @type {String}
     */
    this.slides = this.getAttribute('slides');

    // Get the carousel root.
    if (this.slides !== null) {
      const slidesWrapper = this.querySelector(this.slides);

      if (slidesWrapper !== null) {
        this.carouselRoot = slidesWrapper;
      }
    }

    /**
     * Grid mode enables displaying items in 2 rows by grouping items into slides
     * based on the original perPage option value.
     *
     * @type {Boolean}
     */
    this.hasModeAttrGrid = (this.getAttribute('mode') === 'grid');

    /**
     * The original slides, before any modifications.
     *
     * @type {HTMLCollection}
     */
    this.originalChildren = Array.from(this.carouselRoot.children);

    /**
     * The total number of slides in the carousel.
     *
     * @type {Number}
     */
    this.slideCount = this.originalChildren.length;

    // Bail early if there are no slides.
    if (this.slideCount === 0) {
      this.parentNode.removeChild(this);
      return;
    }

    // Capture pagination wrapper.
    if (this.hasAttribute('pagination')) {
      const pagination = this.getAttribute('pagination');

      /**
       * The list element holding the carousel pagination.
       *
       * @type {HTMLUListElement|HTMLOListElement}
       */
      this.paginationWrapper = document.querySelector(`#${pagination}`);
    }

    /**
     * The parsed `perPage` attribute value.
     *
     * @type {Number|Object}
     */
    this.perPageAttribute = JSON.parse(this.getAttribute('per-page') || 1);

    /**
     * Listen for `perPage` object's breakpoints so we can init/destroy based on
     * the expected number of slides per page.
     */
    if (typeof this.perPageAttribute === 'object') {
      this.perPageOption = Object.keys(this.perPageAttribute)
        .reduce((acc, key, index, perPageKeys) => {
          // Create a MediaQuery for each breakpoint so we can listen for changes.
          const media = (Number(key) === 0)
            ? `(max-width: ${(perPageKeys[index + 1] - 1)}px)`
            : `(min-width: ${key}px)`;

          // 'not all' results from an invalid media query string.
          if (media.media !== 'not all') {
            window.matchMedia(media).addEventListener(
              'change',
              this.conditionallyInit,
            );
          }

          // Update the perPage option to be `1` for grid mode @ medium+ screen size(s).
          if (!this.hasModeAttrGrid || key < 768) {
            return { ...acc, [key]: this.perPageAttribute[key] };
          }

          return { ...acc, [key]: 1 };
        }, {});
    } else {
      // Use the raw attribute value, which should be a number.
      this.perPageOption = this.perPageAttribute;
    }

    /**
     * The media query based upon the `media` attribute.
     *
     * @type {MediaQueryList}
     */
    const media = this.getAttribute('media') || 'all';
    this.media = window.matchMedia(media);

    // Init.
    this.conditionallyInit();
    this.media.addEventListener('change', this.conditionallyInit);
  }

  /**
   * Handle changes to observed attributes.
   *
   * @param {string} name     The attribute name.
   * @param {string} oldValue The attribute's previous value.
   * @param {string} newValue The attribute's current value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (
      undefined !== this.carousel
      && name === 'refresh'
      && newValue === 'true'
    ) {
      fastdom.mutate(() => this.carousel.resizeHandler());
    }
  }

  /**
   * Returns whether grid layout should be used.
   *
   * @return {bool}
   */
  get useGridMode() {
    return (this.hasModeAttrGrid && window.matchMedia('(min-width: 768px)').matches);
  }

  /**
   * Initialize carousel pagination.
   */
  initPagination = () => {
    if (this.hasAttribute('auto-paginate')) {
      this.autoPaginate();
    }

    this.paginateButtons = Array.from(this.paginationWrapper.querySelectorAll('button'));
    if (this.paginateButtons.length > 0) {
      this.paginationWrapper.addEventListener('click', this.onPaginationClick);
    } else {
      this.paginationWrapper.removeEventListener('click', this.onPaginationClick);
    }
  };

  /**
   * Create pagination buttons based on the resolved per-page value.
   */
  autoPaginate = () => {
    // Get the list item + button template.
    const template = document.getElementById('ai-carousel-pagination-list-item');

    if (template !== null) {
      const templateContent = template.content;

      // Calculate the number of pages.
      const denominator = (this.resolvePerPageAttribute() * (this.useGridMode ? 2 : 1));
      const totalPages = Math.ceil(this.slideCount / denominator);

      // Remove any existing pagination.
      this.paginationWrapper.replaceChildren();

      for (let i = 0; i < totalPages; i += 1) {
        try {
          const { currentSlide = 0 } = this.carousel;

          const clonedNode = templateContent.cloneNode(true);
          const button = clonedNode.querySelector('button');
          button.setAttribute('aria-label', `Page ${i + 1}`);

          if (currentSlide === i) {
            button.setAttribute('aria-current', 'true');
          }

          this.paginationWrapper.appendChild(clonedNode);
        } catch (error) {
          console.error(error); // eslint-disable-line no-console
        }
      }
    }
  };

  /**
   * Paginate the carousel.
   *
   * @param {Event} event The Event object.
   */
  onPaginationClick = (event) => {
    const { target } = event;

    const nextSlide = (target.nodeName === 'BUTTON')
      ? this.paginateButtons.indexOf(target)
      : this.paginateButtons.indexOf(target.parentElement); // The img captured the click.

    if (nextSlide > -1) {
      this.carousel.goTo(nextSlide);
    }
  };

  /**
   * Navigate to the previous carousel page.
   */
  onPrevClick() {
    this.carousel.prev(this.carousel.perPage);
  }

  /**
   * Navigate to the next carousel page.
   */
  onNextClick() {
    this.carousel.next(this.carousel.perPage);
  }

  /**
   * Callback for after changing slides.
   */
  onSlideChange() {
    const { currentSlide } = this.carousel;

    this.updateSlideAttributes();
    this.updateCarouselButtons();
    this.updateAriaLiveRegion(currentSlide);

    this.previousSlide = currentSlide;

    const event = new CustomEvent(
      'aiCarousel.slideChange',
      {
        bubbles: true,
        composed: true,
        detail: {
          carousel: this.carousel,
          currentIndex: currentSlide,
          activeSlide: this.carousel.innerElements[currentSlide],
        },
      },
    );

    this.dispatchEvent(event);
  }

  /**
   * Resolve the perPage value based on the window width.
   *
   * @return {Number} The current viewport width's `perPage` value.
   */
  resolvePerPageAttribute() {
    let perPage = this?.carousel?.perPage || 1;

    if (typeof this.perPageAttribute === 'object') {
      const match = Object.keys(this.perPageAttribute)
        .reverse()
        .find((key) => window.matchMedia(`(min-width: ${key}px)`).matches);

      perPage = this.perPageAttribute[match];
    } else if (!Number.isNaN(Number(this.perPageAttribute))) {
      perPage = this.perPageAttribute;
    }

    return perPage;
  }

  /**
   * Conditionally initialize or destroy the carousel.
   */
  conditionallyInit() {
    if (
      this.media.matches
      && this.slideCount > this.resolvePerPageAttribute() * (this.useGridMode ? 2 : 1)
    ) {
      // Do nothing if the carousel is already initialized.
      if (!(this?.carousel instanceof Siema)) {
        this.carouselSetup();
      } else if (this.hasModeAttrGrid) {
        this.updateGridModeSlides();
      }

      /*
       * `paginationWrapper` is only `undefined` if the paginate attribute isn't
       * present, and `null` if the wrapping element isn't found in the DOM.
       */
      if (this.paginationWrapper != null) {
        this.initPagination();
      }
    } else if (this?.carousel instanceof Siema) {
      this.destroy();
      delete this.carousel; // Deleting the reference makes our `instanceof` checks more explicit.
    }
  }

  /**
   * Insert and remove slides as needed to maintain the correct grid layout.
   */
  updateGridModeSlides = () => {
    // Insert new slides.
    const newSlides = this.createGridSlides();

    const newSlidesCount = newSlides.length;
    const slidesCount = this.carousel.innerElements.length;
    const iterations = Math.max(newSlidesCount, slidesCount);

    for (let i = 0; i < iterations; i += 1) {
      if (newSlidesCount > slidesCount) {
        // Insert new slide, conditionally remove the slide it displaces.
        this.carousel.insert(newSlides[i], i, () => {
          if ((i + 1) <= slidesCount) {
            this.carousel.remove(i + 1);
          }
        });
      } else {
        // Remove the first slide and conditionally append a new slide.
        this.carousel.remove(0, () => {
          if (i < newSlidesCount) {
            this.carousel.append(newSlides[i]);
          }
        });
      }
    }

    // Move to the beginning.
    // This compensates for the slider not resetting after replacing slides
    this.carousel.goTo(0);
  };

  /**
   * Returns groupings of items to display in a grid.
   *
   * @return {Array} An array of slides containing 2x the perPage count of items
   */
  createGridSlides = () => {
    if (!this.useGridMode) {
      return this.originalChildren;
    }

    const gridPerSlide = (this.resolvePerPageAttribute() * 2);
    const newChildren = [];

    for (let i = 0; i < this.slideCount; i += gridPerSlide) {
      const div = document.createElement('div');
      div.className = 'ai-carousel__grid-slide';
      div.style.setProperty('--ai-carousel-grid-columns', (gridPerSlide / 2)); // 2 rows.

      const children = this.originalChildren.slice(i, i + gridPerSlide);
      children.forEach((child) => div.appendChild(child.cloneNode(true)));

      newChildren.push(div);
    }

    return newChildren;
  }

  /**
   * Initialize the carousel and controls.
   */
  carouselSetup() {
    if (this.hasModeAttrGrid) {
      /*
       * This works because the carousel hasn't initialized yet. Subsequent
       * changes will require using the Seima API to replace slides.
       */
      this.carouselRoot.replaceChildren(...this.createGridSlides());
    }

    // Initialize the carousel.
    this.carousel = new Siema({
      selector: this.carouselRoot,
      duration: 300,
      draggable: !breakpoints.lgMin.matches,
      perPage: this.perPageOption, // Use the parsed option, rather than the raw attribute value.
      onChange: this.onSlideChange,
    });

    // Call the Siema resizeHandler method to ensure slides are correctly spaced.
    window.addEventListener('load', () => {
      fastdom.mutate(() => this.carousel.resizeHandler());
    }, { once: true });

    // Signal for styling purposes.
    fastdom.mutate(() => this.carouselRoot.classList.add(this.initializedClass));

    /**
     * A reference to what will eventually be the previous slide.
     *
     * @type {Number}
     */
    this.previousSlide = this.carousel.currentSlide;

    /**
     * The previous slide control.
     *
     * @type {HTMLButtonElement}
     */
    this.prev = document.getElementById(this.getAttribute('control-previous'));

    if (this.prev != null) {
      fastdom.mutate(() => this.prev.classList.add('control-previous--initialized'));

      this.prev.addEventListener('click', this.onPrevClick);

      // Previous is disabled until there's a slide to which we can navigate.
      this.prev.setAttribute('aria-disabled', 'true');
    }

    /**
     * The next slide control.
     *
     * @type {HTMLButtonElement}
     */
    this.next = document.getElementById(this.getAttribute('control-next'));

    if (this.next != null) {
      fastdom.mutate(() => this.next.classList.add('control-next--initialized'));

      this.next.addEventListener('click', this.onNextClick);
    }

    /**
     * The aria-live element.
     *
     * @type {HTMLDivElement}
     */
    this.ariaLiveRegion = this.getAriaLiveRegion();

    // Additional carousel attributes.
    if (!this.carouselRoot.hasAttribute('role')) {
      this.carouselRoot.setAttribute('role', 'region');
    }
    this.carouselRoot.tabIndex = 0;

    // Set initial states.
    this.updateSlideAttributes();
    this.updateAriaLiveRegion(0);
  }

  /**
   * Hide and prevent tab focus from leaking into overflow carousel items.
   */
  updateSlideAttributes() {
    const others = Array.from(this.carousel.innerElements);

    // Get the page.
    const current = others.splice(
      this.carousel.currentSlide,
      this.carousel.perPage,
    );

    // Disable tabbing to inactive slide content.
    others.forEach((slide) => {
      slide.setAttribute('tabindex', '-1');
      slide.setAttribute('aria-hidden', true);
      const interactiveChildElements = interactiveChildren(slide);

      // Disable the interactive children of hidden slides.
      interactiveChildElements.forEach((element) => element.setAttribute('tabindex', '-1'));
    });

    // Allow tabbing to active slide content.
    current.forEach((slide) => {
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-hidden');

      const interactiveChildElements = interactiveChildren(slide);

      // Remove the tabindex from children of visible slides.
      interactiveChildElements.forEach((element) => element.removeAttribute('tabindex'));
    });
  }

  /**
   * Updates button attributes based on carousel state.
   */
  updateCarouselButtons() {
    const { currentSlide: currentIndex } = this.carousel;

    // Previous is disabled if the carousel can't be moved back.
    const disablePrevious = (currentIndex === 0);

    // Next is disabled when the carousel can't be advanced.
    const resolvedPerPage = this.resolvePerPageAttribute();
    const disableNext = (this.useGridMode)
      ? (currentIndex + 1) * (resolvedPerPage * 2) >= this.slideCount
      : (currentIndex + resolvedPerPage) >= this.slideCount;

    if (this.prev != null) {
      this.prev.setAttribute('aria-disabled', `${disablePrevious}`);
    }

    if (this.next != null) {
      this.next.setAttribute('aria-disabled', `${disableNext}`);
    }

    this.paginateButtons.forEach((button, idx) => {
      if (idx === currentIndex) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Create and return the aria-live region.
   *
   * @return {HTMLElement} The aria-live region.
   */
  getAriaLiveRegion() {
    const div = document.createElement('div');
    div.setAttribute('aria-live', 'polite');
    div.setAttribute('aria-atomic', 'true');
    div.classList.add('screen-reader-text');

    this.parentNode.insertBefore(div, this.nextSibling);

    return div;
  }

  /**
   * Update ARIA live region to display the currently-active slide.
   *
   * @param {Number} index The current carousel slide index.
   */
  updateAriaLiveRegion(currentSlide) {
    const start = currentSlide + 1;
    const end = this.slideCount > (currentSlide + this.carousel.perPage)
      ? currentSlide + this.carousel.perPage
      : this.slideCount;

    const ariaLiveText = (this.carousel.perPage > 1)
      ? `Items ${start} through ${end} of ${this.slideCount}`
      : `Item ${start} of ${this.slideCount}`;

    this.ariaLiveRegion.textContent = ariaLiveText;
  }

  /**
   * Remove event listeners and elements created by this component.
   */
  cleanup() {
    this.ariaLiveRegion.remove();

    if (this.prev != null) {
      this.prev.removeEventListener('click', this.onPrevClick);
      this.prev.classList.remove('control-previous--initialized');
    }

    if (this.next != null) {
      this.next.removeEventListener('click', this.onNextClick);
      this.next.classList.remove('control-next--initialized');
    }

    // Add original children back.
    this.carouselRoot.replaceChildren(...this.originalChildren);
  }

  /**
   * Called by the browser when the element is removed from the page.
   */
  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Destroy the carousel and cleanup.
   */
  destroy() {
    // Destroy the carousel.
    this.carousel.destroy(
      true,
      () => {
        fastdom.mutate(() => {
          this.carouselRoot.classList.remove(this.initializedClass);
        });
      },
    );

    // Allow tabbing to active slide content.
    Array.from(this.carousel.innerElements).forEach((slide) => {
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-hidden');

      const interactiveChildElements = interactiveChildren(slide);

      // Remove the tabindex from children of slides.
      interactiveChildElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    });

    this.removeAttribute('tabindex');
    this.cleanup();
  }
}

// Define the custom elements.
customElements.define('ai-carousel', AICarousel);
